import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { fetchAllCampaigns } from '../../api/campaigns';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import LoadingSpinner from '../../components/LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCheck } from '@fortawesome/free-solid-svg-icons';

const getCampaignState = (campaign, isLoggedIn) => {
    if (!isLoggedIn) return 'unlocked';
    if (campaign.isLocked) return 'locked';
    if (campaign.isCompleted) return 'completed';
    return 'unlocked';
};

const CampaignTile = ({ campaign, isLoggedIn }) => {
    const state = getCampaignState(campaign, isLoggedIn);
    const heroStadium = campaign.heroStadium || 'tutorial';
    const heroSrc = `/img/stadiums/${heroStadium}.png`;

    return (
        <Link
            to={`/campaigns/${campaign.campaignID}`}
            className={`campaign-tile campaign-tile--${state}`}
        >
            <div className="campaign-tile-card">
                {state === 'locked' && (
                    <span className="campaign-tile-lock-icon">
                        <FontAwesomeIcon icon={faLock} />
                    </span>
                )}
                {state === 'completed' && (
                    <span className="campaign-tile-check-icon">
                        <FontAwesomeIcon icon={faCheck} />
                    </span>
                )}
                <img
                    src={heroSrc}
                    alt={`${campaign.name} stadium`}
                    className="campaign-tile-stadium"
                    onError={e => { e.target.src = '/img/transparent.png'; }}
                />
                <span className="campaign-tile-name">
                    {campaign.name}
                    {campaign.hasConstraints && (
                        <span className="campaign-challenge-badge" title="Challenge Campaign">⚔️</span>
                    )}
                </span>
                {campaign.desc && <span className="campaign-tile-desc">{campaign.desc}</span>}
                {isLoggedIn && campaign.totalContestCount > 0 && (
                    <span className="campaign-tile-progress">
                        {campaign.completedContestCount ?? 0} of {campaign.totalContestCount} contests
                    </span>
                )}
            </div>
        </Link>
    );
};

CampaignTile.propTypes = {
    campaign: PropTypes.shape({
        campaignID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        desc: PropTypes.string,
        isLocked: PropTypes.bool,
        isCompleted: PropTypes.bool,
        completedContestCount: PropTypes.number,
        totalContestCount: PropTypes.number,
    }).isRequired,
    isLoggedIn: PropTypes.bool,
};

const ViewCampaignsPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchAllCampaigns();
                setCampaigns(data);
            } catch (err) {
                setError('Error fetching campaigns');
                toast.error('Error fetching campaigns: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) return <LoadingSpinner />;
    if (error) return <p className="error">{error}</p>;

    const isLoggedIn = !!currentUser;

    return (
        <div className="view-campaigns-page">
            <h2 className="view-campaigns-title">Campaigns</h2>
            {currentUser?.isAdmin && (
                <div>
                    <Link to="/campaigns/create" className="button">Create Campaign</Link>
                    {' '}
                    <Link to="/campaign-contests/create" className="button">Create Campaign Contests</Link>
                </div>
            )}
            {campaigns.length === 0 ? (
                <p>No campaigns found.</p>
            ) : (
                <div className="campaigns-grid">
                    {campaigns.map(campaign => (
                        <CampaignTile key={campaign.campaignID} campaign={campaign} isLoggedIn={isLoggedIn} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ViewCampaignsPage;
